<template>
    <div class="block xl:flex flex-row gap-x-6 xl:w-3/5 xl:pr-4">
        <!-- Column 1 -->
        <div class="xl:w-1/2">
            <div class="flex gap-x-4 justify-between items-end mb-4">
                <div class="flex-1">
                    <jet-label for="name" value="Name" class=""/>
                    <jet-input type="text" class="block w-full" v-model="form.name" ref="name" />
                    <jet-input-error :message="$page.props.errors.name" class="mt-1" />
                </div>

                <div>
                    <toggle id="enabled" class="py-1" v-model="form.enabled" />
                    <jet-label for="enabled" value="Active" class="ml-2 inline-block" />
                </div>
            </div>

            <div class="mb-4">
                <jet-label for="address_1" value="Address 1"/>
                <jet-input type="text" class="block w-full" v-model="form.address_1" />
                <jet-input-error :message="$page.props.errors.address_1" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="address_2" value="Address 2"/>
                <jet-input type="text" class="block w-full" v-model="form.address_2" />
                <jet-input-error :message="$page.props.errors.description" class="mt-1" />
            </div>

            <div class="flex gap-x-4 justify-between mb-4">
                <div class="flex-1">
                    <jet-label for="city" value="City"/>
                    <jet-input type="text" class="block w-full" v-model="form.city" />
                    <jet-input-error :message="$page.props.errors.city" class="mt-1" />
                </div>

                <div class="flex-1">
                    <jet-label for="postal_code" value="Postal Code" class="whitespace-nowrap"/>
                    <jet-input type="text" class="block w-full" v-model="form.postal_code" />
                    <jet-input-error :message="$page.props.errors.postal_code" class="mt-1" />
                </div>

                <div class="flex-1">
                    <jet-label for="province" value="Province"/>
                    <multiselect class="multi-lg"
                        v-model="form.province"
                        :options="provinceOptions"
                        :classes="{singleLabel: 'w-full px-2'}"
                    />
                    <jet-input-error :message="$page.props.errors.province" class="mt-1" />
                </div>
            </div>

            <div class="mb-4">
                <jet-label for="phone" value="Phone"/>
                <jet-input type="tel" class="block w-full" v-model="form.phone" />
                <jet-input-error :message="$page.props.errors.description" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="cell" value="Cell"/>
                <jet-input type="tel" class="block w-full" v-model="form.cell" />
                <jet-input-error :message="$page.props.errors.cell" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="fax" value="Fax"/>
                <jet-input type="tel" class="block w-full" v-model="form.fax" />
                <jet-input-error :message="$page.props.errors.fax" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="email" value="Email"/>
                <jet-input type="text" class="block w-full" v-model="form.email" />
                <span class="text-xs text-gray-400 leading-tight">Separate multiple email addresses with a comma.</span>
                <jet-input-error :message="$page.props.errors.email" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="ap_email" value="AP Email"/>
                <jet-input type="email" class="block w-full" v-model="form.ap_email" />
                <jet-input-error :message="$page.props.errors.ap_email" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="contact_name" value="Contact Name"/>
                <jet-input type="text" class="block w-full" v-model="form.contact_name" />
                <jet-input-error :message="$page.props.errors.contact_name" class="mt-1" />
            </div>

            <div class="mb-4">
                <toggle id="show_picksheet_pricing" class="py-1" v-model="form.show_picksheet_pricing" />
                <jet-label for="show_picksheet_pricing" value="Show Picksheet Pricing" class="ml-2 inline-block" />
            </div>
        </div>

        <!-- Column 2 -->
        <div class="xl:w-1/2 flex-1 gap-y-4">
            <div class="mb-4">
                <jet-label for="type" value="Type" />
                <multiselect
                    v-model="form.type"
                    :options="typeOptions"
                    :classes="{ singleLabel: 'w-full px-2' }"
                    class="multi-lg"
                    placeholder="Select type"
                />
                <jet-input-error :message="$page.props.errors.type" class="mt-1" />
            </div>

            <div class="flex gap-x-4 justify-between items-end mb-4">
                <div class="flex-1">
                    <jet-label for="pricing_level" value="Pricing Level" />
                    <multiselect
                        v-model="form.pricing_level_id"
                        :options="pricingOptions"
                        placeholder="Select level"
                        :classes="{singleLabel: 'w-full px-2'}"
                        class="multi-lg"
                    />
                    <jet-input-error :message="$page.props.errors.pricing_level_id" class="mt-1" />
                </div>
                <div>
                    <toggle id="tax_exempt" class="py-1" v-model="form.tax_exempt" />
                    <jet-label for="tax_exempt" value="Tax Exempt" class="ml-2 inline-block" />
                </div>
            </div>

            <div v-if="isAdmin && form.type === 'School'" class="flex gap-x-4 justify-between items-end mb-4">
                <div class="flex-1">
                    <jet-label for="credit_balance" value="Prepaid & Fundraising Balance" />
                    <div class="flex">
                        <span class="pt-1 px-3 border border-r-0 rounded-r-none rounded-l">$</span>
                        <jet-input type="number" min="0" step="0.01" v-numbers v-model="form.credit_balance" class="w-full rounded-l-none"></jet-input>
                    </div>
                    <jet-input-error :message="$page.props.errors.credit_balance" class="mt-1" />
                </div>
                <div>
                    <toggle id="auto_apply_credit" class="py-1" v-model="form.auto_apply_credit" />
                    <jet-label for="auto_apply_credit" value="Auto Apply Balance" class="ml-2 inline-block" />
                </div>
            </div>

            <div class="flex gap-x-6 justify-between mb-4">
                <div class="flex-1">
                    <jet-label for="payment_terms" value="Payment Terms" class="whitespace-nowrap"/>
                    <multiselect
                        v-model="form.payment_terms"
                        :options="paymentTermsOptions"
                        :classes="{singleLabel: 'w-full px-2'}"
                        :canClear="false"
                        :canDeselect="false"
                        placeholder="Select terms"
                        class="multi-lg"
                    />
                </div>

                 <div class="flex-1">
                    <jet-label for="payment_method" value="Payment Method" class="whitespace-nowrap"/>
                    <multiselect
                    v-model="form.payment_method"
                    :options="paymentMethodOptions"
                    :classes="{singleLabel: 'w-full px-2'}"
                    :canClear="false"
                    :canDeselect="false"
                    placeholder="Select method"
                    class="multi-lg"
                    />
                </div>
            </div>

            <div class="flex gap-x-6 justify-between mb-4">
                <div class="flex-1">
                    <jet-label for="default_shipping_method" value="Default Shipping Method" class="whitespace-nowrap"/>
                    <multiselect
                        v-model="form.default_shipping_method"
                        :options="shippingOptions"
                        :classes="{singleLabel: 'w-full px-2'}"
                        :canClear="false"
                        :canDeselect="false"
                        class="multi-lg"
                    />
                </div>

                 <div class="flex-1">
                    <jet-label for="route" value="Routific Route" />
                    <multiselect
                        v-model="form.route"
                        :options="routeOptions"
                        :classes="{singleLabel: 'w-full px-2'}"
                        class="multi-lg"
                        placeholder="Select route"
                    />
                    <jet-input-error :message="$page.props.errors.route" class="mt-1" />
                </div>
            </div>

            <div class="flex gap-x-6 justify-between mb-4">
                <div class="flex-1">
                    <jet-label for="delivery_days" value="Delivery Days" />
                    <multiselect
                        v-model="form.delivery_days"
                        :options="deliveryOptions"
                        mode="multiple"
                        :closeOnSelect="false"
                        placeholder="Select days"
                        :classes="{multipleLabel: 'w-full px-2'}"
                        class="multi-lg"
                    >
                        <template v-slot:multiplelabel="{ values }">
                            <div class="multiselect-multiple-label">
                                {{ values.length }} day{{ values.length > 1 ? 's' : ''}}
                            </div>
                        </template>
                    </multiselect>
                    <div v-show="form.delivery_days.length">
                        <span v-for="(day,index) in form.delivery_days" :key="day" class="text-xs">
                            {{day}}{{index == form.delivery_days.length - 1 ? '' : ', '}}
                        </span>
                    </div>
                    <jet-input-error :message="$page.props.errors.delivery_days" class="mt-1" />
                </div>

                <div class="flex-1">
                    <jet-label for="delivery_duration" value="Delivery Duration (minutes)" />
                    <jet-input type="number" v-model="form.delivery_duration" class="w-full" :disabled="!isLogistics && form.id !== null" min="1"></jet-input>
                    <jet-input-error :message="$page.props.errors.delivery_duration" class="mt-1" />
                </div>
            </div>

            <div class="flex gap-x-6 justify-between mb-4">
                <div class="flex-1">
                    <jet-label for="delivery_window_start" value="Delivery Window Start" />
                    <jet-input type="time" v-model="form.delivery_window_start" class="w-full" :disabled="!isLogistics && form.id !== null" min="1" id="delivery_window_start" />
                </div>

                <div class="flex-1">
                    <jet-label for="delivery_window_end" value="Delivery Window End" />
                    <jet-input type="time" v-model="form.delivery_window_end" class="w-full" :disabled="!isLogistics && form.id !== null" min="1" id="delivery_window_end" />
                </div>
            </div>

            <div class="mb-4">
                <jet-label for="delivery_order_minimum" value="Delivery Order Minimum"/>
                <div class="flex">
                    <span class="pt-1 px-3 border border-r-0 rounded-r-none rounded-l">$</span>
                    <jet-input type="text" class="w-full rounded-l-none" v-model="form.delivery_order_minimum"/>
                </div>
                <div class="text-xs text-gray-500">Leave blank to use the default delivery order minimum.</div>
                <jet-input-error :message="$page.props.errors.delivery_order_minimum" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="delivery_fee" value="Delivery Fee"/>
                <div class="flex">
                    <span class="pt-1 px-3 border border-r-0 rounded-r-none rounded-l">$</span>
                    <jet-input type="text" class="w-full rounded-l-none" v-model="form.delivery_fee"/>
                </div>
                <jet-input-error :message="$page.props.errors.delivery_fee" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="sales_rep_id" value="Sales Rep:"/>
                <multiselect
                    v-model="form.sales_rep_id"
                    valueProp="id"
                    label="name"
                    :options="salesReps"
                    :classes="{ singleLabel: 'w-full px-2' }"
                    class="multi-lg"
                />
                <jet-input-error :message="$page.props.errors.sales_rep_id" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="order_notes" value="Order Notes / Shipping Instructions"/>
                <textarea class="block w-full resize-none" rows="4" v-model="form.order_notes" />
                <jet-input-error :message="$page.props.errors.order_notes" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="internal_notes" value="Internal Notes"/>
                <textarea class="block w-full resize-none" rows="4" v-model="form.internal_notes" />
                <jet-input-error :message="$page.props.errors.internal_notes" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="load_notes" value="Load Notes"/>
                <textarea class="block w-full resize-none" rows="4" v-model="form.load_notes" :disabled="!isLogistics && form.id !== null" />
                <jet-input-error :message="$page.props.errors.load_notes" class="mt-1" />
            </div>
        </div>
    </div>
</template>

<script>
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import Multiselect from '@vueform/multiselect';
import Toggle from '@vueform/toggle';
import { usePage } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';

export default {
    props: [
        'form', 
        'salesReps', 
        'typeOptions', 
        'deliveryOptions', 
        'routeOptions', 
        'pricingOptions', 
        'paymentTermsOptions',
        'paymentMethodOptions'
    ],

    components: {
        JetLabel,
        JetInput,
        JetInputError,
        Multiselect,
        Toggle,
    },

    setup () {
        const { isLogistics, isAdmin, isCustomer }   = UseUserAuth(usePage().props.value.user);

        return {
            isLogistics,
            isAdmin,
            isCustomer
        }
        
    },

    data() {
        return {
            provinceOptions: [
                'Ontario',
                'Manitoba',
                'Saskatchewan',
                'British Columbia',
                'Yukon',
                'Northwest Territories',
                'Nunavut',
                'Quebec',
                'Nova Scotia',
                'New Brunswick',
                'Newfoundland',
                'Prince Edward Island'
            ],

            shippingOptions: [
                { value: 'delivery', label: 'Delivery' },
                { value: 'pickup', label: 'Pickup' },
            ],
        }
    },

    mounted () {
        if (this.form.id === null) {
            this.$refs.name.focus();
        }
    }
}
</script>

<style>

</style>
